import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  FormControl,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
  Typography,
} from '@airbus/components-react';
import { MoreVert } from '@airbus/icons/react';
import SaveIcon from '@mui/icons-material/Save';
import { useContext, useEffect, useState } from 'react';
import { t } from '../../../../i18next';
import { useConfigContext } from '../../../context/ConfigContext';
import { useColumnContextAPI } from '../../../context/GetAllColumnContext';
import { UserContext } from '../../../context/UserContext';
import { deleteQuery } from '../../../services/deleteViewManager';
import { updateView } from '../../../services/updateViewManager';
import AddViews from './AddViews';
import { useGlobalViewPermission } from './useGlobalViewPermission';
import styles from './ViewsActions.module.css';
import SaveAsRoundedIcon from '@mui/icons-material/SaveAsRounded';
import PublicIcon from '@mui/icons-material/Public';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShareIcon from '@mui/icons-material/Share';

export default function ViewsIcons(props) {
  const { apiRef, setAlertbar, isUpdateing, setIsUpdateing, saveWidthsCheckBox } = props;
  const { userDetails } = useContext(UserContext);
  const { configSchema } = useConfigContext();
  const [openConfirmDeleteBox, setOpenConfirmDeleteBox] = useState(false);
  const [openConfirmUpdateBox, setOpenConfirmUpdateBox] = useState(false);
  const [isOpenAddView, setIsOpenAddView] = useState(false);
  const [isGlobalViewActionDisable, setIsGlobalViewActionDisable] = useState(false);
  const [notPermissionForGlobalActivity, setNotPermissionForGlobalActivity] = useState(false);
  const [isOpenSaveAsAGlobalViewDialog, setIsOpenSaveAsAGlobalViewDialog] = useState(false);
  const [isOpenUpdateAsAGlobalViewDialog, setIsUpdateSaveAsAGlobalViewDialog] = useState(false);
  const [isOpenRenameViewDialog, setIsRenameGlobalViewDialog] = useState(false);
  const [viewName, setViewName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isGlobal, setIsGlobal] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');

  const {
    tableDetails,
    STANDARD_VIEW,
    selectedViewName,
    setSelectedViewName,
    selectedView,
    setSelectedView,
    setInitialViewColumns,
    getViewManagerData,
  } = useColumnContextAPI();

  useGlobalViewPermission({
    selectedView,
    userDetails,
    setIsGlobalViewActionDisable,
    setNotPermissionForGlobalActivity,
  });

  const deleteViewHandler = async () => {
    setIsUpdateing(true);
    deleteQuery(selectedView.id, userDetails.userId).then(() => {
      setIsUpdateing(false);
      setSelectedViewName(STANDARD_VIEW);
      getViewManagerData(10, userDetails?.userId);
      setAlertbar({ children: t('deleted_successfully'), severity: 'success' });
      setOpenConfirmDeleteBox(false);
    });
  };

  const updateHandler = async () => {
    if (!selectedView) {
      setAlertbar({ children: t('view.no_selected'), severity: 'error' });
      return;
    }
    if (selectedViewName === STANDARD_VIEW) {
      setOpenConfirmUpdateBox(true);
      return;
    }
    if (selectedView.isGlobal) {
      setIsUpdateSaveAsAGlobalViewDialog(true);
      return;
    }
    commitViewHandler();
  };

  useEffect(() => {
    setViewName(selectedView.viewName);
    setIsDefault(selectedView.defaultView);
    setIsGlobal(selectedView.isGlobal);
  }, [selectedView]);

  const commitViewHandler = async ({ IsGlobal = isGlobal, IsDefault = isDefault } = {}) => {
    if (!selectedView || !apiRef.current) {
      setAlertbar({ children: t('view.save_failed'), severity: 'error' });
      return;
    }
    apiRef.current.flushSync?.();
    const exportState = apiRef.current.exportState();
    const dimensions = exportState.columns?.dimensions || {};
    const allColumns = apiRef.current.getAllColumns();

    allColumns.forEach((col) => {
      if (col.field?.startsWith('__')) return;

      const width = col.computedWidth ?? col.width;
      if (typeof width === 'number' && !isNaN(width)) {
        if (!dimensions[col.field]) dimensions[col.field] = {};
        if (saveWidthsCheckBox) {
          dimensions[col.field].width = width;
        } else {
          dimensions[col.field].width = 150;
        }
      } else {
        console.error(`Width undefined for [${col.field}]`, col);
      }
    });
    const orderedFields = (exportState.columns?.orderedFields || []).filter(
      (item) => item !== 'undoDelete' && item !== 'id',
    );

    const viewColumnsWithWidths: string[] = [];
    orderedFields.forEach((field) => {
      viewColumnsWithWidths.push(field);
      const width = dimensions?.[field]?.width;
      if (typeof width === 'number') {
        viewColumnsWithWidths.push(`width:${width}px`);
      }
    });

    const payload = {
      viewColumns: viewColumnsWithWidths,
      isGlobal: IsGlobal,
      viewName: viewName,
      defaultView: IsDefault,
      gridFilter: exportState,
    };
    setIsUpdateing(true);
    const res = await updateView(payload, selectedView.id);
    setIsUpdateing(false);
    if (!res?.[`update_${configSchema}_mbd_view_manager_many`]?.[0]?.['affected_rows']) {
      setAlertbar({ children: t('view.save_failed'), severity: 'error' });
      return;
    }
    const updatedViews = await getViewManagerData(tableDetails?.id, userDetails?.userId);
    const refreshedView = updatedViews?.find((view) => view.id === selectedView.id);
    if (refreshedView) {
      setSelectedView({
        ...refreshedView,
        gridFilter: exportState,
      });
    } else {
      setSelectedView((prev) => ({
        ...prev,
        gridFilter: exportState,
      }));
    }

    setInitialViewColumns(orderedFields);
    if (!notPermissionForGlobalActivity) {
      if (!IsGlobal) {
        setAlertbar({ children: t('view.save_successfully'), severity: 'success' });
      } else {
        setAlertbar({
          title: t('global.view_updated'),
          children: t('global.the_selected_global_been_updated'),
          severity: 'success',
        });
      }
    } else {
      setAlertbar({ children: t('view.save_successfully'), severity: 'success' });
    }
    setIsGlobalViewNameIsDefaultToSelectedView();
  };

  const filteredColumns = selectedView.viewColumns.filter(
    (item) =>
      !['undoDelete', 'id', tableDetails.mbdDataGrid.dataGridUniqueIdentifier].includes(item),
  );
  const isNoColumnSelected = !filteredColumns.length;
  let isDisabled = selectedViewName === STANDARD_VIEW;
  if (userDetails.roleIds.includes(1)) {
    isDisabled = false;
  }

  const setIsGlobalViewNameIsDefaultToSelectedView = () => {
    setViewName(selectedView.viewName);
    setIsDefault(selectedView.defaultView);
    setIsGlobal(selectedView.isGlobal);
  };

  return (
    <>
      <div className={styles.iconInnerRow}>
        <Tooltip label={t('view.save_new_view')}>
          <IconButton
            id='adding-view'
            variant='ghost'
            aria-haspopup='dialog'
            disabled={isUpdateing || isNoColumnSelected}
            onClick={() => {
              setIsOpenAddView(true);
              setDialogMode('add');
            }}
            className={styles.iconButtonStyle}
          >
            <SaveAsRoundedIcon fontSize='small' />
          </IconButton>
        </Tooltip>

        <Tooltip label={t('save')}>
          <IconButton
            variant='ghost'
            onClick={updateHandler}
            disabled={isGlobalViewActionDisable || isDisabled || isUpdateing || isNoColumnSelected}
            className={styles.iconButtonStyle}
          >
            <SaveIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        {/* —— Mobile-only inline actions (≤480px) ——  {`${styles.mobileActions} mobileActions`}*/}
        <div className={`${styles.mobileActions} mobileActions`}>
          <Tooltip label={t('global.share')}>
            <IconButton
              variant='ghost'
              disabled={
                isGlobalViewActionDisable ||
                selectedViewName === STANDARD_VIEW ||
                isUpdateing ||
                selectedView.isGlobal
              }
              onClick={() => {
                setIsOpenAddView(true);
                setDialogMode('share');
              }}
              className={styles.iconButtonStyle}
            >
              <ShareIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          {!notPermissionForGlobalActivity && (
            <Tooltip label={t('global.set_as_global_view')}>
              <IconButton
                variant='ghost'
                disabled={
                  isGlobalViewActionDisable ||
                  selectedViewName === STANDARD_VIEW ||
                  isUpdateing ||
                  selectedView.isGlobal
                }
                onClick={() => setIsOpenSaveAsAGlobalViewDialog(true)}
                className={styles.iconButtonStyle}
              >
                <PublicIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip label={t('global.rename')}>
            <IconButton
              variant='ghost'
              disabled={
                isGlobalViewActionDisable ||
                selectedViewName === STANDARD_VIEW ||
                isUpdateing ||
                isNoColumnSelected
              }
              onClick={() => setIsRenameGlobalViewDialog(true)}
              className={styles.iconButtonStyle}
            >
              <DriveFileRenameOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>

          <Tooltip label={t('global.delete')}>
            <IconButton
              variant='ghost'
              disabled={
                isGlobalViewActionDisable || selectedViewName === STANDARD_VIEW || isUpdateing
              }
              onClick={() => setOpenConfirmDeleteBox(true)}
              className={styles.iconButtonStyle}
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </div>

        {/* —— Desktop menu (>480px) —— */}
        <div className={styles.desktopActions}>
          <Menu>
            <MenuButton
              icon={<MoreVert className={styles.iconBlue} />}
              variant='ghost'
              size='large'
              aria-label='Open Menu'
              data-testid='menu-button'
              className={`${styles.iconButtonStyle} ${styles.menuIconButton}`}
            />
            <MenuList>
              <MenuItem
                icon={<ShareIcon fontSize='small' className={styles.iconBlue} />}
                data-testid='menu-button-global'
                disabled={
                  isGlobalViewActionDisable ||
                  selectedViewName === STANDARD_VIEW ||
                  isUpdateing ||
                  selectedView.isGlobal
                }
                onClick={() => {
                  setIsOpenAddView(true);
                  setDialogMode('share');
                }}
              >
                {t('view.share')}
              </MenuItem>
              {!notPermissionForGlobalActivity && (
                <MenuItem
                  icon={<PublicIcon fontSize='small' className={styles.iconBlue} />}
                  data-testid='menu-button-global'
                  disabled={
                    isGlobalViewActionDisable ||
                    selectedViewName === STANDARD_VIEW ||
                    isUpdateing ||
                    selectedView.isGlobal
                  }
                  onClick={() => {
                    setIsOpenSaveAsAGlobalViewDialog(true);
                  }}
                >
                  {t('global.set_as_global_view')}
                </MenuItem>
              )}
              <MenuItem
                icon={<DriveFileRenameOutlineIcon fontSize='small' className={styles.iconBlue} />}
                disabled={
                  isGlobalViewActionDisable ||
                  selectedViewName === STANDARD_VIEW ||
                  isUpdateing ||
                  isNoColumnSelected
                }
                onClick={() => {
                  setIsRenameGlobalViewDialog(true);
                }}
              >
                {t('global.rename')}
              </MenuItem>

              <MenuItem
                icon={<DeleteOutlineIcon fontSize='small' className={styles.iconBlue} />}
                disabled={
                  isGlobalViewActionDisable || selectedViewName === STANDARD_VIEW || isUpdateing
                }
                onClick={() => setOpenConfirmDeleteBox(true)}
              >
                {t('global.delete')}
              </MenuItem>
            </MenuList>
          </Menu>
        </div>
      </div>

      <AddViews
        {...{ isOpenAddView, setIsOpenAddView, setAlertbar, isUpdateing, setIsUpdateing }}
        mode={dialogMode}
      />

      <ConfirmDeleteBox
        {...{
          openConfirmDeleteBox,
          setOpenConfirmDeleteBox,
          selectedViewName,
          deleteViewHandler,
          isUpdateing,
        }}
      />
      <ConfirmStandardViewUpdateBox
        {...{ commitViewHandler, openConfirmUpdateBox, setOpenConfirmUpdateBox }}
      />
      <SetAsGlobalView
        {...{
          isOpenSaveAsAGlobalViewDialog,
          setIsOpenSaveAsAGlobalViewDialog,
          commitViewHandler,
        }}
      />

      <UpdateAsGlobalView
        {...{
          isOpenUpdateAsAGlobalViewDialog,
          setIsUpdateSaveAsAGlobalViewDialog,
          commitViewHandler,
        }}
      />

      <RenameView
        {...{
          isOpenRenameViewDialog,
          setIsRenameGlobalViewDialog,
          commitViewHandler,
          selectedViewName,
          selectedView,
          viewName,
          setViewName,
          isDefault,
          setIsDefault,
          isGlobal,
          setIsGlobal,
          isNoColumnSelected,
          notPermissionForGlobalActivity,
        }}
      />
    </>
  );
}

function ConfirmDeleteBox(props) {
  const {
    openConfirmDeleteBox,
    setOpenConfirmDeleteBox,
    selectedViewName,
    deleteViewHandler,
    deleteLoading,
  } = props;
  return (
    <Dialog
      open={openConfirmDeleteBox}
      onClose={() => setOpenConfirmDeleteBox(false)}
      aria-describedby='alert-dialog-slide-description'
      className={styles.dialogZIndex}
    >
      <DialogHeader>
        <Typography variant='h6'>{t('view.confirm_delete')}</Typography>
      </DialogHeader>
      <DialogContent>
        {t('view.delete_selected_view', { viewName: selectedViewName })}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenConfirmDeleteBox(false)}>{t('cancel')}</Button>
        <Button onClick={deleteViewHandler} variant='primary' disabled={deleteLoading}>
          {t('ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
function ConfirmStandardViewUpdateBox(props) {
  const { commitViewHandler, openConfirmUpdateBox, setOpenConfirmUpdateBox } = props;
  const okHandler = () => {
    commitViewHandler();
    setOpenConfirmUpdateBox(false);
  };
  return (
    <Dialog
      open={openConfirmUpdateBox}
      onClose={() => setOpenConfirmUpdateBox(false)}
      aria-describedby='alert-dialog-slide-description'
      className={styles.dialogZIndex}
    >
      <DialogHeader>
        <Typography variant='h6'>{t('view.update_standard_view')}</Typography>
      </DialogHeader>
      <DialogContent>{t('view.do_you_want_update_standard_view')}</DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenConfirmUpdateBox(false)}>{t('cancel')}</Button>
        <Button variant='primary' onClick={okHandler}>
          {t('ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const SetAsGlobalView = (props) => {
  const { isOpenSaveAsAGlobalViewDialog, setIsOpenSaveAsAGlobalViewDialog, commitViewHandler } =
    props;
  const okSetGlobalView = () => {
    setIsOpenSaveAsAGlobalViewDialog(false);
    commitViewHandler({ IsGlobal: true });
  };
  return (
    <Dialog
      open={isOpenSaveAsAGlobalViewDialog}
      onClose={() => {
        setIsOpenSaveAsAGlobalViewDialog(false);
      }}
      aria-describedby='alert-dialog-slide-description'
      className={styles.dialogZIndex}
    >
      <DialogHeader>
        <Typography variant='h6'>{t('global.set_as_global_view')}</Typography>
      </DialogHeader>
      <DialogContent>
        {t('global.do_you_want_to_set_this_view_as_global_view?')}
        <br />
        {t('global.this_will_make_it_accessible_to_all_users')}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsOpenSaveAsAGlobalViewDialog(false)} variant='secondary'>
          {t('cancel')}
        </Button>
        <Button onClick={okSetGlobalView} variant='primary'>
          {t('ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const UpdateAsGlobalView = (props) => {
  const { isOpenUpdateAsAGlobalViewDialog, setIsUpdateSaveAsAGlobalViewDialog, commitViewHandler } =
    props;
  const okSetGlobalView = () => {
    setIsUpdateSaveAsAGlobalViewDialog(false);
    commitViewHandler();
  };
  return (
    <Dialog
      open={isOpenUpdateAsAGlobalViewDialog}
      onClose={() => {
        setIsUpdateSaveAsAGlobalViewDialog(false);
      }}
      aria-describedby='alert-dialog-slide-description'
      className={styles.dialogZIndex}
    >
      <DialogHeader>
        <Typography variant='h6'>{t('global.update_global_view')}</Typography>
      </DialogHeader>
      <DialogContent>
        {t('global.do_you_want_to_set_this_view_as_global_view?')}
        <br />
        {t('global.changes_made_to_this_view_will_be_applied_for_all_users')}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsUpdateSaveAsAGlobalViewDialog(false)} variant='secondary'>
          {t('cancel')}
        </Button>
        <Button onClick={okSetGlobalView} variant='primary'>
          {t('global.update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const RenameView = (props) => {
  const {
    isOpenRenameViewDialog,
    setIsRenameGlobalViewDialog,
    commitViewHandler,
    selectedView,
    viewName,
    setViewName,
    isDefault,
    setIsDefault,
    isGlobal,
    setIsGlobal,
    notPermissionForGlobalActivity,
  } = props;
  const { STANDARD_VIEW, viewData } = useColumnContextAPI();
  const [error, setError] = useState(false);
  const okSetRenameView = () => {
    setIsRenameGlobalViewDialog(false);
    commitViewHandler();
    setViewName('');
  };

  useEffect(() => {
    if (isOpenRenameViewDialog) {
      setViewName(selectedView.viewName ?? '');
      setIsDefault(selectedView.defaultView ?? false);
      setIsGlobal(selectedView.isGlobal ?? false);
    }
  }, [isOpenRenameViewDialog, selectedView.viewName, selectedView.defaultView]);

  const viewNameChangeHandler = (e) => {
    setViewName(e.target.value);
    const arr = viewData?.filter((item) => item.viewName === e.target.value);
    if (!arr.length && e.target.value.toLowerCase() !== STANDARD_VIEW.toLowerCase()) {
      setError(false);
      return;
    }
    setError(true);
  };
  return (
    <Dialog
      open={isOpenRenameViewDialog}
      onClose={() => setIsRenameGlobalViewDialog(false)}
      aria-describedby='alert-dialog-slide-description'
      className={styles.dialogZIndex}
    >
      <DialogHeader>
        <Typography variant='h6'>{t('global.edit_view_name')}</Typography>
      </DialogHeader>
      <DialogContent>
        <FormControl
          label={t('view.view_name')}
          required
          error={error}
          errorText={error && t('view.name_already')}
        >
          <Input id='outlined-basic' value={viewName} onChange={viewNameChangeHandler} />
        </FormControl>
        <div className={styles.renameCheckBoxGroup}>
          <Checkbox
            label={t('view.set_default')}
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />

          {!notPermissionForGlobalActivity && (
            <span className={styles.checkboxPromptLeft}>
              <Checkbox
                style={{}}
                label={t('Set Global')}
                checked={isGlobal}
                onChange={(e) => setIsGlobal(e.target.checked)}
              />
            </span>
          )}
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setIsRenameGlobalViewDialog(false)} variant='secondary'>
          {t('cancel')}
        </Button>
        <Button onClick={okSetRenameView} variant='primary' disabled={error}>
          {t('ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
